import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Check } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppButton } from "../components/WhatsAppButton";
import { MobileNav } from "../components/MobileNav";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { whatsAppSimple } from "../lib/api";
import { useRequestModal } from "../context/RequestModalContext";
import { useI18n } from "../i18n/LanguageContext";
import { SERVICES, CITIES, buildPage, pagePath, hubPath, SITE } from "../lib/seoPages";

const T = {
  en: {
    included: "What we arrange", pricing: "Indicative pricing", faq: "Frequently asked",
    request: "Make a request", whatsapp: "WhatsApp concierge",
    otherCities: "Same service, other destinations", otherServices: "Other services here",
    allServices: "All services & destinations", home: "Home",
  },
  es: {
    included: "Qué organizamos", pricing: "Precios orientativos", faq: "Preguntas frecuentes",
    request: "Hacer una solicitud", whatsapp: "Concierge por WhatsApp",
    otherCities: "Mismo servicio, otros destinos", otherServices: "Otros servicios aquí",
    allServices: "Todos los servicios y destinos", home: "Inicio",
  },
};

export default function ServiceCityPage({ serviceId, cityId, lang }) {
  const { openRequest } = useRequestModal();
  const { lang: uiLang, setLang } = useI18n();
  const service = SERVICES.find((s) => s.id === serviceId);
  const city = CITIES.find((c) => c.id === cityId);
  const p = buildPage(lang, service, city);
  const t = T[p.lang];

  useEffect(() => { window.scrollTo(0, 0); }, [serviceId, cityId, lang]);
  useEffect(() => { if (lang !== uiLang) setLang(lang); }, [lang, uiLang, setLang]);

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "Service",
      name: p.h1, serviceType: p.serviceShort, description: p.description,
      url: `${SITE}${p.path}`,
      areaServed: { "@type": "Place", name: p.cityName },
      provider: {
        "@type": "ProfessionalService", name: "VELMONTÉ", url: SITE,
        telephone: "+34645459043", email: "office@velmonte.com",
        priceRange: "€€€€", areaServed: CITIES.map((c) => c.en.name),
        availableLanguage: ["en", "es", "ar", "de", "fr"],
      },
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: p.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: t.home, item: SITE },
        { "@type": "ListItem", position: 2, name: t.allServices, item: `${SITE}${hubPath(p.lang)}` },
        { "@type": "ListItem", position: 3, name: p.h1, item: `${SITE}${p.path}` },
      ],
    },
  ];

  const prefill = `${p.serviceShort} — ${p.cityName}: `;

  return (
    <div className="vm-grain relative">
      <Seo title={p.title} description={p.description} path={p.path} lang={p.lang} altPath={p.altPath} image={p.img} jsonLd={jsonLd} />
      <Header />
      <main data-testid="service-city-page">
        <section className="relative min-h-[62vh] md:min-h-[72vh] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img src={p.img} alt={p.h1} className="w-full h-full object-cover scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-vm-bg via-vm-bg/50 to-black/50" />
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
            <nav className="vm-label mb-5 flex items-center gap-2 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-vm-text" data-testid="breadcrumb-home">{t.home}</Link>
              <span className="text-vm-accent">/</span>
              <Link to={hubPath(p.lang)} className="hover:text-vm-text" data-testid="breadcrumb-hub">{p.label}</Link>
            </nav>
            <motion.h1 className="font-serif font-light leading-[0.98] text-4xl md:text-6xl lg:text-7xl max-w-4xl"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} data-testid="landing-h1">
              {p.h1}
            </motion.h1>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button onClick={() => openRequest(p.cat, prefill)} className="vm-btn-primary px-9 py-4 text-xs tracking-[0.2em] uppercase" data-testid="landing-request-button">
                {t.request}
              </button>
              <a href={whatsAppSimple(`VELMONTÉ — ${prefill}`)} target="_blank" rel="noopener noreferrer"
                className="vm-btn-ghost px-9 py-4 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2" data-testid="landing-whatsapp-button">
                <MessageCircle strokeWidth={1.5} size={16} /> {t.whatsapp}
              </a>
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28 border-b border-vm-border">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <Reveal>
              <p className="text-lg md:text-xl text-vm-text2 font-light leading-relaxed" data-testid="landing-intro">{p.intro}</p>
            </Reveal>
          </div>
        </section>

        <section className="py-20 md:py-28 border-b border-vm-border">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <Reveal><p className="vm-label mb-8">{t.included}</p></Reveal>
            <ul className="grid md:grid-cols-2 gap-x-12 gap-y-6">
              {p.bullets.map((b, i) => (
                <Reveal key={i} delay={(i % 2) * 0.06}>
                  <li className="flex gap-4 text-vm-text2 font-light leading-relaxed" data-testid={`landing-bullet-${i}`}>
                    <Check strokeWidth={1.3} size={18} className="text-vm-accent shrink-0 mt-1" />
                    <span>{b}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal>
              <div className="mt-14 border border-vm-border p-7 md:p-9">
                <p className="vm-label mb-4">{t.pricing}</p>
                <p className="text-vm-text2 font-light leading-relaxed text-sm md:text-base" data-testid="landing-pricing">{p.price}</p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-20 md:py-28 border-b border-vm-border">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <Reveal>
              <p className="vm-label mb-6">{t.faq}</p>
              <h2 className="font-serif font-light leading-[1.05] text-3xl md:text-4xl mb-10">{p.serviceShort} · {p.cityName}</h2>
            </Reveal>
            <div className="divide-y divide-vm-border border-t border-vm-border">
              {p.faqs.map((f, i) => (
                <Reveal key={i} delay={(i % 3) * 0.05}>
                  <details className="group py-6" data-testid={`landing-faq-${i}`}>
                    <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                      <span className="font-serif text-xl md:text-2xl font-light">{f.q}</span>
                      <span className="text-vm-accent text-2xl leading-none shrink-0 transition-transform duration-300 group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-4 text-vm-text2 font-light leading-relaxed text-sm md:text-base">{f.a}</p>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-14">
            <div>
              <p className="vm-label mb-6">{t.otherCities}</p>
              <div className="flex flex-wrap gap-3">
                {CITIES.filter((c) => c.id !== cityId).map((c) => (
                  <Link key={c.id} to={pagePath(p.lang, service[p.lang].slug, c.id)}
                    className="text-xs tracking-[0.15em] uppercase px-4 py-2 border border-vm-border text-vm-text2 hover:border-vm-accent hover:text-vm-text transition-colors"
                    data-testid={`link-city-${c.id}`}>
                    {c[p.lang].name}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <p className="vm-label mb-6">{t.otherServices}</p>
              <div className="flex flex-wrap gap-3">
                {SERVICES.filter((s) => s.id !== serviceId).map((s) => (
                  <Link key={s.id} to={pagePath(p.lang, s[p.lang].slug, cityId)}
                    className="text-xs tracking-[0.15em] uppercase px-4 py-2 border border-vm-border text-vm-text2 hover:border-vm-accent hover:text-vm-text transition-colors"
                    data-testid={`link-service-${s.id}`}>
                    {s[p.lang].short}
                  </Link>
                ))}
              </div>
              <Link to={hubPath(p.lang)} className="vm-label inline-block mt-8 hover:text-vm-text" data-testid="link-hub">
                {t.allServices} →
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileNav />
    </div>
  );
}
